/**
 * Member Entries — read-only.
 *
 * Members' own advice is never editable from the admin; this page exists to
 * answer "what is actually being created, and does it get shared?".
 *
 * Pagination is SERVER-side: the grid's rowCount comes from the envelope's
 * `pagination.total` and every page change refetches. Status and pillar filters
 * are server-side too (the service accepts `status`, `category_id`, `user_id`).
 *
 * The service has no date-range or free-text parameters, so the shared time
 * filter and the search input refine the rows on the current page only — that is
 * called out in the UI rather than faked. A search value that is a UUID is
 * promoted to the server-side `user_id` filter, which is the common case
 * ("show me everything this member wrote").
 *
 * The pill set carries an extra `All` in front of the canonical six. The two date
 * fields it replaces were optional and empty by default, i.e. "every row on this
 * page"; without an All pill the page would silently start hiding older entries,
 * which would change what the screen reports rather than only how it is picked.
 */

import React from 'react';
import {
  Box,
  Button,
  Alert,
  Chip,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Drawer,
  Typography,
  Divider,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';

import EmptyState from '../../common/EmptyState';
import TimeRangeFilter from '../../common/TimeRangeFilter';
import useTimeRangeParam from '../../common/useTimeRangeParam';
import { DAY_PRESETS, TimeRangePreset, resolveRange } from '../../common/timeRange';
import { brandColors } from '../../../theme/brandColors';
import {
  lifeAdviceApi,
  WisdomCategory,
  WisdomEntryRow,
  WisdomEntryDetail,
} from '../../../services/lifeAdviceAdminService';
import {
  PageHeader,
  errorMessage,
  writeErrorMessage,
  isBackendDown,
  formatDate,
  formatDateTime,
} from '../common/lifeAdviceUi';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The canonical six, with the unbounded default this screen has always had. */
const ENTRY_PRESETS: TimeRangePreset[] = [{ token: 'all', label: 'All' }, ...DAY_PRESETS];

const STATUSES = ['draft', 'recorded', 'transformed', 'shared'];

const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success'> = {
  draft: 'default',
  recorded: 'info',
  transformed: 'warning',
  shared: 'success',
};

const MemberEntriesPage: React.FC = () => {
  const [categories, setCategories] = React.useState<WisdomCategory[]>([]);
  const [rows, setRows] = React.useState<WisdomEntryRow[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [down, setDown] = React.useState(false);
  const [toast, setToast] = React.useState<string | null>(null);

  const [statusFilter, setStatusFilter] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [range, setRange] = useTimeRangeParam<'all'>({ type: 'all' }, { presets: ENTRY_PRESETS });
  const [includeText, setIncludeText] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);

  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });

  const [detail, setDetail] = React.useState<WisdomEntryDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState<string | null>(null);

  // A UUID typed into the search box is a member id — send it upstream.
  const userIdFilter = UUID_RE.test(search.trim()) ? search.trim() : undefined;

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setDown(false);
    try {
      const page = await lifeAdviceApi.entries.list({
        ...(categoryFilter ? { category_id: categoryFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(userIdFilter ? { user_id: userIdFilter } : {}),
        limit: paginationModel.pageSize,
        page: paginationModel.page + 1,
        sort: 'created_at',
        direction: 'desc',
      });
      setRows(page.rows);
      setTotal(page.pagination.total);
    } catch (e) {
      setDown(isBackendDown(e));
      setError(errorMessage(e, 'Failed to load member entries.'));
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter, userIdFilter, paginationModel.page, paginationModel.pageSize]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  React.useEffect(() => {
    lifeAdviceApi.categories
      .list()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  // Client-side refinement of the current page (see the file header).
  const visible = React.useMemo(() => {
    const needle = userIdFilter ? '' : search.trim().toLowerCase();
    const bounds = range.type === 'all' ? null : resolveRange(range);
    const from = bounds ? bounds.from.getTime() : null;
    const to = bounds ? bounds.to.getTime() : null;
    return rows.filter((r) => {
      const created = new Date(r.created_at).getTime();
      if (from !== null && created < from) return false;
      if (to !== null && created > to) return false;
      if (!needle) return true;
      return (
        (r.title || '').toLowerCase().includes(needle) ||
        (r.recipient_name || '').toLowerCase().includes(needle) ||
        (r.recipient_relation || '').toLowerCase().includes(needle) ||
        r.user_id.toLowerCase().includes(needle)
      );
    });
  }, [rows, search, userIdFilter, range]);

  const clientFiltered = visible.length !== rows.length;

  const openDetail = async (id: string) => {
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    try {
      setDetail(await lifeAdviceApi.entries.get(id));
    } catch (e) {
      setDetailError(errorMessage(e, 'Could not load this entry.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const csv = await lifeAdviceApi.entries.exportCsv({
        ...(categoryFilter ? { category_id: categoryFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        include_text: includeText,
      });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lifeadvice-entries.csv';
      a.click();
      URL.revokeObjectURL(url);
      setToast(
        includeText
          ? 'Exported with member advice text — this export is logged.'
          : 'Exported entry metadata (no advice text).'
      );
    } catch (e) {
      setToast(writeErrorMessage(e, 'Export failed.'));
    } finally {
      setExporting(false);
    }
  };

  const columns: GridColDef<WisdomEntryRow>[] = [
    {
      field: 'created_at',
      headerName: 'Created',
      width: 130,
      valueFormatter: (value) => formatDate(value as string),
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<WisdomEntryRow, string | null>) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
          {params.value || <em style={{ opacity: 0.6 }}>Untitled</em>}
        </Typography>
      ),
    },
    {
      field: 'category_name',
      headerName: 'Pillar',
      width: 180,
      valueGetter: (_value, row) => row.category_name || row.category_slug || '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams<WisdomEntryRow, string>) => (
        <Chip
          size="small"
          label={params.value}
          color={STATUS_COLOR[String(params.value)] || 'default'}
          variant="outlined"
        />
      ),
    },
    { field: 'word_count', headerName: 'Words', width: 90, type: 'number' },
    {
      field: 'has_ai_version',
      headerName: 'AI',
      width: 80,
      renderCell: (params: GridRenderCellParams<WisdomEntryRow, boolean>) =>
        params.value ? <Chip size="small" label="AI" color="info" variant="outlined" /> : <span>—</span>,
    },
    {
      field: 'has_voice_clone',
      headerName: 'Voice',
      width: 90,
      renderCell: (params: GridRenderCellParams<WisdomEntryRow, boolean>) =>
        params.value ? (
          <Chip size="small" label="Cloned" color="secondary" variant="outlined" />
        ) : (
          <span>—</span>
        ),
    },
    {
      field: 'recipient_relation',
      headerName: 'For',
      width: 140,
      valueGetter: (_value, row) => row.recipient_relation || '—',
    },
    {
      field: 'shared_via',
      headerName: 'Shared via',
      width: 130,
      valueGetter: (_value, row) => row.shared_via || '—',
    },
    {
      field: 'user_id',
      headerName: 'Member',
      width: 160,
      renderCell: (params: GridRenderCellParams<WisdomEntryRow, string>) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {String(params.value).slice(0, 8)}…
        </Typography>
      ),
    },
  ];

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (down) {
    return (
      <EmptyState
        icon="error"
        title="LifeAdvice service is unreachable"
        description={`The admin API could not reach lifescribe-advice. ${error || ''}`}
        action={{ label: 'Retry', onClick: reload, variant: 'contained' }}
      />
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={<PeopleIcon sx={{ fontSize: 32, color: brandColors.forestGreen }} />}
        title="Member Entries"
        subtitle="Read-only. What members have recorded, what the AI transformed, and what actually got sent."
        helpId="lifeadvice-entries"
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <TimeRangeFilter
              value={range}
              onChange={setRange}
              presets={ENTRY_PRESETS}
              onRefresh={reload}
              refreshing={loading}
            />
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              onClick={exportCsv}
              disabled={exporting}
            >
              {exporting ? 'Exporting…' : 'Export CSV'}
            </Button>
          </Stack>
        }
      />

      {error && !down && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={reload}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
        <TextField
          select
          label="Status"
          size="small"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPaginationModel((m) => ({ ...m, page: 0 }));
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Pillar"
          size="small"
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value);
            setPaginationModel((m) => ({ ...m, page: 0 }));
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All pillars</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.icon} {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Title, recipient, or a member UUID"
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          label="Export text"
          size="small"
          value={includeText ? 'yes' : 'no'}
          onChange={(e) => setIncludeText(e.target.value === 'yes')}
          sx={{ minWidth: 200 }}
          helperText="Including advice text is logged"
        >
          <MenuItem value="no">Metadata only</MenuItem>
          <MenuItem value="yes">Include advice text</MenuItem>
        </TextField>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Status, pillar and member-UUID filters run on the server across all {total} entries. The date
        range and free-text search refine the page you are looking at.
        {clientFiltered && ` Showing ${visible.length} of ${rows.length} rows on this page.`}
      </Typography>

      {!loading && total === 0 && !error ? (
        <EmptyState
          icon="empty_box"
          title="No member entries yet"
          description="Nothing has been recorded against these filters. Clear the filters or check back once members start creating advice."
          action={{
            label: 'Clear filters',
            onClick: () => {
              setStatusFilter('');
              setCategoryFilter('');
              setSearch('');
              setRange({ type: 'all' });
            },
            variant: 'outlined',
          }}
        />
      ) : (
        <Box sx={{ height: 620, width: '100%' }}>
          <DataGrid
            rows={visible}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            disableRowSelectionOnClick
            onRowClick={(params) => openDetail(String(params.id))}
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100]}
          />
        </Box>
      )}

      {/* ── Detail drawer ────────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={detailLoading || !!detail || !!detailError}
        onClose={() => {
          setDetail(null);
          setDetailError(null);
        }}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 560 }, p: 3 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
            Entry detail
          </Typography>
          <IconButton
            aria-label="Close entry detail"
            onClick={() => {
              setDetail(null);
              setDetailError(null);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {detailLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {detailError && <Alert severity="error">{detailError}</Alert>}

        {detail && (
          <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              {detail.title || 'Untitled'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Chip
                size="small"
                label={detail.status}
                color={STATUS_COLOR[String(detail.status)] || 'default'}
              />
              {detail.category_name && <Chip size="small" label={detail.category_name} variant="outlined" />}
              {detail.recipient_relation && (
                <Chip size="small" label={`For: ${detail.recipient_relation}`} variant="outlined" />
              )}
              {detail.has_voice_clone && <Chip size="small" label="Voice cloned" variant="outlined" />}
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Member {detail.user_id} · created {formatDateTime(detail.created_at)} ·{' '}
              {detail.word_count} words
              {detail.prompt_title ? ` · prompt: ${detail.prompt_title}` : ''}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              What the member said
            </Typography>
            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 3, backgroundColor: brandColors.softCream, borderRadius: '10px' }}
            >
              <Typography
                sx={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: 15,
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {detail.original_text || detail.transcription_text || '— nothing captured yet —'}
              </Typography>
            </Paper>

            {detail.transformed_text && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  AI transformed version
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: '10px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Georgia, "Times New Roman", serif',
                      fontSize: 15,
                      lineHeight: 1.7,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {detail.transformed_text}
                  </Typography>
                </Paper>
              </>
            )}

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Share history
            </Typography>
            {detail.shares && detail.shares.length > 0 ? (
              <Stack spacing={1}>
                {detail.shares.map((s) => (
                  <Paper key={s.id} variant="outlined" sx={{ p: 1.5, borderRadius: '10px' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {s.share_method}
                      {s.recipient_identifier ? ` → ${s.recipient_identifier}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Sent {formatDateTime(s.created_at)} ·{' '}
                      {s.opened_at ? `opened ${formatDateTime(s.opened_at)}` : 'not opened yet'}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Never shared.
              </Typography>
            )}
          </Box>
        )}
      </Drawer>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default MemberEntriesPage;
